import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Megaphone, PartyPopper, UtensilsCrossed, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-zinc-100 placeholder-zinc-500 outline-none transition-colors focus:border-neon-amber/60 focus:ring-1 focus:ring-neon-amber/40";

const empty = {
  kind: "event", name: "", city: "phl", neighborhood: "", description: "",
  submitter_name: "", submitter_email: "", date: "", time: "", venue: "",
  price: "", category: "Live", style: "Late-Night Eats", hours: "", featured_dish: "", image: "",
};

export default function SubmitModal({ open, onClose, cities }) {
  const [form, setForm] = useState(empty);
  const [flyer, setFlyer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const liveCities = cities.length ? cities : [{ id: "phl", name: "Philadelphia", state: "PA" }];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let image = form.image;
      if (flyer) {
        const fd = new FormData();
        fd.append("file", flyer);
        const { data: up } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        image = `${process.env.REACT_APP_BACKEND_URL}/api/files/${up.path}`;
      }
      await api.post("/submissions", { ...form, image });
      setDone(true);
      toast.success("Tip received — we'll review it.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setDone(false);
    setForm(empty);
    setFlyer(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
          onClick={close} data-testid="submit-modal">
          <motion.div initial={{ y: 60, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="my-8 w-full max-w-lg rounded-2xl border border-white/10 bg-[#0E1117] p-6 sm:p-8">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-neon-amber">Community Intel</div>
                <h3 className="mt-2 font-display text-xl font-bold uppercase tracking-tight">Tip Us Off</h3>
                <p className="mt-1 text-sm text-zinc-500">Reviewed by a human before anything goes live.</p>
              </div>
              <button onClick={close} data-testid="submit-modal-close"
                className="rounded-full border border-white/10 p-2 text-zinc-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            {done ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-6 text-center" data-testid="submit-success">
                <Megaphone className="mx-auto h-10 w-10 text-neon-amber" />
                <h4 className="mt-4 font-display text-lg font-bold uppercase">Intel received.</h4>
                <p className="mt-2 text-sm text-zinc-400">
                  If it passes the vibe check, it goes live on the city page. We may email you for details.
                </p>
                <button onClick={close} data-testid="submit-done-button"
                  className="mt-6 rounded-full bg-neon-amber px-8 py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5">
                  Done
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "event", label: "Event / Party", icon: PartyPopper },
                    { v: "spot", label: "Food / Drink Spot", icon: UtensilsCrossed },
                  ].map((t) => (
                    <button type="button" key={t.v} onClick={() => setForm((f) => ({ ...f, kind: t.v }))}
                      data-testid={`submit-kind-${t.v}`}
                      className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-all ${
                        form.kind === t.v
                          ? "border-neon-amber/60 bg-neon-amber/10 text-neon-amber"
                          : "border-white/10 text-zinc-500 hover:text-white"
                      }`}>
                      <t.icon className="h-4 w-4" /> {t.label}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <input required value={form.name} onChange={set("name")} placeholder="Name" data-testid="submit-name-input" className={inputCls} />
                  <select value={form.city} onChange={set("city")} data-testid="submit-city-select" className={inputCls}>
                    {liveCities.map((c) => (
                      <option key={c.id} value={c.id} className="bg-[#0E1117]">{c.name}{c.state ? `, ${c.state}` : ""}</option>
                    ))}
                  </select>
                </div>
                <input required value={form.neighborhood} onChange={set("neighborhood")} placeholder="Neighborhood" data-testid="submit-neighborhood-input" className={inputCls} />

                {form.kind === "event" ? (
                  <div className="grid grid-cols-2 gap-4">
                    <input required type="date" value={form.date} onChange={set("date")} data-testid="submit-date-input" className={inputCls} />
                    <input required value={form.time} onChange={set("time")} placeholder="Time (e.g. 10:00 PM)" data-testid="submit-time-input" className={inputCls} />
                    <input required value={form.venue} onChange={set("venue")} placeholder="Venue" data-testid="submit-venue-input" className={inputCls} />
                    <select value={form.category} onChange={set("category")} data-testid="submit-category-select" className={inputCls}>
                      {["Live", "Club", "Food", "Art", "LGBTQ+", "Lifestyle (LS)"].map((c) => <option key={c} value={c} className="bg-[#0E1117]">{c}</option>)}
                    </select>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <select value={form.style} onChange={set("style")} data-testid="submit-style-select" className={inputCls}>
                      {["Street Food", "Speakeasy", "Late-Night Eats", "Rooftop Bar"].map((s) => <option key={s} value={s} className="bg-[#0E1117]">{s}</option>)}
                    </select>
                    <input required value={form.hours} onChange={set("hours")} placeholder="Hours (e.g. till 3AM)" data-testid="submit-hours-input" className={inputCls} />
                    <input value={form.featured_dish} onChange={set("featured_dish")} placeholder="Must-order item (optional)" data-testid="submit-dish-input" className={`${inputCls} col-span-2`} />
                  </div>
                )}

                <textarea required value={form.description} onChange={set("description")} rows={3}
                  placeholder="Why should out-of-towners care? Sell it in two sentences." data-testid="submit-description-input" className={inputCls} />
                <label className="block cursor-pointer rounded-lg border border-dashed border-white/20 px-4 py-3 text-center transition-colors hover:border-neon-amber/50" data-testid="submit-flyer-dropzone">
                  <input type="file" accept="image/*" className="hidden" data-testid="submit-flyer-input"
                    onChange={(e) => setFlyer(e.target.files?.[0] || null)} />
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
                    {flyer ? `Flyer attached: ${flyer.name}` : "Attach flyer image (optional — JPG/PNG, 5MB max)"}
                  </span>
                </label>
                <input value={form.image} onChange={set("image")} placeholder="...or paste an image URL" data-testid="submit-image-input" className={inputCls} />

                <div className="grid grid-cols-2 gap-4">
                  <input required value={form.submitter_name} onChange={set("submitter_name")} placeholder="Your name" data-testid="submit-submitter-name" className={inputCls} />
                  <input required type="email" value={form.submitter_email} onChange={set("submitter_email")} placeholder="Your email" data-testid="submit-submitter-email" className={inputCls} />
                </div>

                <button type="submit" disabled={loading} data-testid="submit-form-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-neon-amber py-3.5 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(245,158,11,0.4)] disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
                  {loading ? "Sending intel" : "Send the Tip"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
